import { ReactNode } from "react";
import { Navigate, useParams } from "react-router-dom";
import { Loading } from "@/components/atoms";
import { ClientScopeProvider } from "@/components/pages/Client/settings/ClientScopeContext";
import { useGetAccessQuery } from "@/redux/apis/assistants/assistantsApi";
import { AssistantBookingBanner } from "./AssistantBookingBanner";

/**
 * Shared shell for the EA's on-behalf-of booking surfaces (DEV-1296). Resolves `:accessId` to the
 * managed client's account uuid (as ManageClientTakeover does), gates on a CONNECTED link, and hosts
 * its children under ClientScopeProvider with the "Booking for {client}" banner. Keeping the
 * resolve + CONNECTED gate here means the booking sheet and its confirmation share one gate rather
 * than two copies. A failed/unauthorized resolve or a non-CONNECTED link bounces back to My Clients.
 */
export const AssistantBookingScope = ({ children }: { children: ReactNode }) => {
  const { accessId = "" } = useParams<{ accessId: string }>();
  const { data: access, isLoading, isError } = useGetAccessQuery(accessId, { skip: !accessId });

  // Loading is absolutely positioned, so give it a box to fill.
  if (isLoading) {
    return (
      <div className="relative flex-1">
        <Loading />
      </div>
    );
  }

  if (isError || !access || access.status !== "CONNECTED" || !access.party.uuid) {
    return <Navigate to="/client/my-clients" replace />;
  }

  return (
    <ClientScopeProvider accountId={access.party.uuid} clientName={access.party.name}>
      <div className="flex min-h-0 flex-1 flex-col">
        <AssistantBookingBanner name={access.party.name} />
        {children}
      </div>
    </ClientScopeProvider>
  );
};
